import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { alpha, Badge, Box, Collapse, Divider, List, ListItemButton, Stack, Typography, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { NAV_ITEMS_LIST } from './constants';
import { CustomListItemButton, MenuBox, MenuIconBox, NavPaper, NavBottomButton } from './style';
import { useAuth } from '../../hooks/useAuth';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { isPastDate } from '../../utils/date';

function Nav({ showSmallSizeMenu, setShowSmallSizeMenu, displayDetails }) {
    return (
        <NavPaper
            component={'nav'}
            sx={{
                minWidth: showSmallSizeMenu ? 75 : 280,
                maxWidth: showSmallSizeMenu ? 75 : 280,
            }}
        >
            <MenuBoxUI showSmallSizeMenu={showSmallSizeMenu} setShowSmallSizeMenu={setShowSmallSizeMenu} displayDetails={displayDetails} />
        </NavPaper>
    );
}

export const MenuBoxUI = ({ showSmallSizeMenu, setShowSmallSizeMenu , displayDetails}) => {
    return (
        <MenuBox>
            <RenderMenu2 displayDetails={displayDetails}/>
            <BottomMenu />
        </MenuBox>
    );
};

export const RenderMenu2 = ({ showSmallSizeMenu, displayDetails = {} }) => {
    const {paymentDetails = [], displayPrimeAssessment = false, displayPrimeMailsCount = 0 } = displayDetails || {}

    const { getUserDetailsNotAsync } = useAuth();
    const userDetails = getUserDetailsNotAsync();

    const location = useLocation();

    const primeServicesList = NAV_ITEMS_LIST.filter((cNav) => cNav.relatedTo === 'PRIME SERVICES' && (displayPrimeAssessment || cNav.path !== '/prime-assessment-list'));
    const businessToolsList = NAV_ITEMS_LIST.filter((cNav) => cNav.relatedTo === 'BUSINESS TOOLS');

    const isAnyPaymentDelayed = () => {
        return paymentDetails.some((payemntDetail) => {
            if (payemntDetail?.nextScheduledPayment) {
                return isPastDate(payemntDetail.nextScheduledPayment);
            }
            return false;
        });
    };

    return (
        <Stack
            component="ul"
            spacing={0.5}
            sx={{
                listStyleType: 'none',
            }}
        >
            <Box>
                <Typography component={'h1'} fontFamily={'fontFamilyCondensed'} color={'text.primary'} fontWeight={700} fontSize={'1rem'} marginBottom={'4px'}>
                    PRIME SERVICES
                </Typography>
                {primeServicesList.map((item) => (
                    <React.Fragment key={item.title}>
                        {item.list ? (
                            <NavItemWithCollapse item={item} showSmallSizeMenu={showSmallSizeMenu} location={location} userDetails={userDetails} showDot={isAnyPaymentDelayed()} />
                        ) : (
                            <NavItem item={item} showSmallSizeMenu={showSmallSizeMenu} location={location} displayPrimeMailsCount={displayPrimeMailsCount}/>
                        )}
                    </React.Fragment>
                ))}
            </Box>

            <Box>
                <Typography component={'h1'} fontFamily={'fontFamilyCondensed'} color={'text.primary'} fontWeight={700} fontSize={'1rem'} marginTop={'1rem'} marginBottom={'4px'}>
                    BUSINESS TOOLS
                </Typography>
                {businessToolsList.map((item) => (
                    <React.Fragment key={item.title}>
                        {item.list ? (
                            <NavItemWithCollapse item={item} showSmallSizeMenu={showSmallSizeMenu} location={location} userDetails={userDetails} showDot={isAnyPaymentDelayed()} />
                        ) : (
                            <NavItem item={item} showSmallSizeMenu={showSmallSizeMenu} location={location} />
                        )}
                    </React.Fragment>
                ))}
            </Box>
        </Stack>
    );
};

export const BottomMenu = ({ showSmallSizeMenu }) => {
    const location = useLocation();

    const theme = useTheme();

    const handleClick = () => {
        window.location.href = '/signout';
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
            }}
        >
            <Divider sx={{ width: '100%', marginBottom: '0.5rem' }} />
            <NavBottomButton
                component={Link}
                to={'/customer-support'}
                selected={location.pathname.includes('/customer-support')}
                showSmallSizeMenu={showSmallSizeMenu}
                startIcon={<HelpOutlineIcon color="text.secondary" />}
            >
                {!showSmallSizeMenu && 'Customer Support'}
            </NavBottomButton>
            <NavBottomButton
                component={Link}
                to={'/contact-sales'}
                selected={location.pathname.includes('/contact-sales')}
                showSmallSizeMenu={showSmallSizeMenu}
                startIcon={<PhoneOutlinedIcon color="text.secondary" />}
            >
                {!showSmallSizeMenu && 'Contact Sales'}
            </NavBottomButton>

            {/* Remove logout button */}
            {/* <NavBottomButton showSmallSizeMenu={showSmallSizeMenu} onClick={handleClick} startIcon={<PowerSettingsNewIcon color="text.secondary" />}>
                {!showSmallSizeMenu && 'Logout'}
            </NavBottomButton> */}
        </Box>
    );
};

function NavItem({ item, showSmallSizeMenu, location, displayPrimeMailsCount }) {
    return (
        <Box component={'li'}>
            <CustomListItemButton
                disableRipple
                selected={item.path === location.pathname || item.otherPaths?.indexOf(location.pathname) > -1}
                component={Link}
                to={item.path}
                className={`${showSmallSizeMenu && 'showSmall'}`}
            >
                <MenuIconBox className="icon" component="span">
                    {item.icon}
                </MenuIconBox>

                {!showSmallSizeMenu && (
                    <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
                        {item.title}
                    </Box>
                )}
                {item.path === '/mail' && displayPrimeMailsCount > 0 && (
                    <Badge
                        badgeContent={displayPrimeMailsCount}
                        color="error"
                        sx={{
                            '& .MuiBadge-badge': {
                                right: '-15px',
                                top: '0px',
                            },
                        }}
                    >
                    </Badge>
                )}
            </CustomListItemButton>
        </Box>
    );
}

function NavItemWithCollapse({ item, showSmallSizeMenu, location, userDetails, showDot = false }) {
    const [open, setOpen] = useState(false);

    const theme = useTheme();

    const handleClick = () => {
        setOpen(!open);
    };

    item.list =
        item?.title === 'My Companies'
            ? [...(userDetails?.businessList || [])]
                  .map((c) => {
                      return {
                          ...c,
                          path: `/${c.id}`,
                          title: `${c.accountName}`,
                      };
                  })
                  .sort((companyA, companyB) => companyA.accountName.localeCompare(companyB.accountName))
            : [...(item?.list || [])];

    const getDot = (sx = {}) => {
        return (
            <Box
                className="dot"
                sx={{
                    display: 'inline-block',
                    height: '6px',
                    width: '6px',
                    borderRadius: '50%',
                    background: 'red',
                    marginLeft: '2px',
                    ...sx,
                }}
            ></Box>
        );
    };

    const setDefaultValue = ()=>{
        setOpen(item?.title === 'My Companies'? true : false)
    }

    useEffect(() => {
        // open collapse if current path selected on first load
        location.pathname.includes(item.path) && handleClick();
        item?.title === 'My Companies' && setDefaultValue();
    }, []);

    const shouldShowDot = item.path === '/payment-information' && showDot;

    return (
        <Box
            component={'li'}
            sx={(theme) => ({
                ...(open &&
                    location.pathname.includes(item.path) && {
                        fontWeight: theme.typography.fontWeightBold,
                        border: `2px solid ${alpha('#000', 0.1)}`,

                        borderRadius: theme.typography.borderRadius.xxxSmall,

                        backgroundColor: theme.palette.background.neutralSecondary,

                        color: theme.palette.text.primary,

                        '& .icon': {
                            // opacity: '100%',
                            // color: theme.palette.primary.main,
                        },
                        '&:hover': {
                            backgroundColor: theme.palette.background.neutralSecondary,
                        },
                    }),
            })}
        >
            <CustomListItemButton
                disableRipple
                onClick={handleClick}
                component={Link}
                selected={!open && location.pathname.includes(item.path)}
                // to={item.path}
                // className={` ${item.path === location.pathname && 'active'} ${showSmallSizeMenu && 'showSmall'}`}
                className={`${showSmallSizeMenu && 'showSmall'}`}
            >
                <MenuIconBox className="icon" component="span">
                    {item.icon}
                </MenuIconBox>

                {!showSmallSizeMenu && (
                    <Box component="span" sx={{ whiteSpace: 'pre-line', flex: 1 }}>
                        {item.title} {shouldShowDot ? getDot() : null}
                    </Box>
                )}
                {open ? <ExpandLess sx={{ color: theme.palette.primary.main }} /> : <ExpandMore />}
            </CustomListItemButton>

            {item.list && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {item.list.map((cListItem) => {
                            const shouldShowDot = cListItem.path === '/payment-methods' && showDot;
                            return (
                                <Box component={'div'} key={cListItem.path}>
                                    <ListItemButton
                                        disableRipple
                                        component={Link}
                                        to={`${item.path}${cListItem.path}`}
                                        selected={`${item.path}${cListItem.path}` === location.pathname}
                                        className={`${showSmallSizeMenu && 'showSmall'}`}
                                        collapse={String(true)}
                                        sx={(theme) => ({
                                            backgroundColor: 'unset',

                                            '&.Mui-selected': {
                                                backgroundColor: 'unset',

                                                '& span': {
                                                    color: theme.palette.primary.main,
                                                },
                                                '&:hover': {
                                                    backgroundColor: 'unset',
                                                },
                                            },
                                            '&:hover': {
                                                backgroundColor: 'unset',
                                            },
                                        })}
                                    >
                                        {!showSmallSizeMenu && (
                                            <Typography
                                                component="span"
                                                fontSize={'customFontSize.xSmall'}
                                                color={'text.secondary'}
                                                sx={{ whiteSpace: 'pre-line', flex: 1, paddingLeft: '3rem', display: 'flex', alignItems: 'center' }}
                                            >
                                                {cListItem.title}{' '}
                                                {shouldShowDot
                                                    ? getDot({
                                                          height: '4px',
                                                          width: '4px',
                                                          marginLeft: '4px',
                                                      })
                                                    : null}
                                            </Typography>
                                        )}
                                    </ListItemButton>
                                </Box>
                            );
                        })}
                    </List>
                </Collapse>
            )}
        </Box>
    );
}
export default Nav;
